import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import DeviceContext from '@totem/components/devices/deviceDetails/DeviceContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import IBDIcon from '@totem/styles/Icon';
import { DeviceKey } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
const DeviceKeysTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { deviceData, loading, onAction } = useContext(DeviceContext);
  const [keys, setKeys] = useState<DeviceKey[]>([]);
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);
  useEffect(() => {
    if (
      isNotNull(deviceData) &&
      isNotNull(deviceData.device) &&
      isNotNull(deviceData.device.keys)
    ) {
      setKeys(deviceData.device.keys);
    } else {
      setKeys([]);
    }
  }, [deviceData]);
  const sendDeviceKeyUpdate = useCallback(
    async (deviceId: string, update: DeviceKey) => {
      if (isSendingUpdate) {
        return;
      }
      setIsSendingUpdate(true);

      fetch(`${DEVICES_ENDPOINT}/${deviceId}/keys/createdBy`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(update),
      }).then(() => {
        setIsSendingUpdate(false);

        onAction('refresh');
      });
    },
    [isSendingUpdate],
  );
  const handleKeyUpdate = (newState: string, key: DeviceKey) => {
    key.createdBy = newState;

    sendDeviceKeyUpdate(deviceData.device.id, key);
  };
  const columns: ColumnProps<DeviceKey>[] = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      defaultSortOrder: 'ascend',
      render: (_, rec: DeviceKey) => {
        if (rec.type === 'MAC_ADDRESS') {
          return (
            <Tag color={'default'} variant={'outlined'}>
              {rec.type}
            </Tag>
          );
        }

        return (
          <Tag color={'blue'} variant={'outlined'}>
            {rec.type}
          </Tag>
        );
      },
      sorter: (compA, compB) =>
        compA.type === compB.type ? 0 : compA.type > compB.type ? 1 : -1,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      sorter: (compA, compB) =>
        compA.value === compB.value ? 0 : compA.value > compB.value ? 1 : -1,
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      defaultSortOrder: 'ascend',
      render: (_, rec: DeviceKey) => {
        if (rec.apiSiteId !== '') {
          return (
            <Tag color={'success'} variant={'outlined'}>
              API Site
            </Tag>
          );
        }
        if (rec.apiConnectionId !== '') {
          return (
            <Tag color={'blue'} variant={'outlined'}>
              API Connection
            </Tag>
          );
        }
        if (rec.tenantId !== '') {
          return (
            <Tag color={'error'} variant={'outlined'}>
              LEGACY TENANT
            </Tag>
          );
        }
        if (rec.apiConfigId !== '') {
          return (
            <Tag color={'error'} variant={'outlined'}>
              LEGACY CONNECTION
            </Tag>
          );
        }

        return (
          <Tag color={'default'} variant={'outlined'}>
            N/A
          </Tag>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (compA, compB) =>
        compA.createdAt === compB.createdAt
          ? 0
          : compA.createdAt > compB.createdAt
            ? 1
            : -1,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: (compA, compB) =>
        compA.createdBy === compB.createdBy
          ? 0
          : compA.createdBy > compB.createdBy
            ? 1
            : -1,
      render: (_, rec: DeviceKey) => {
        if (rec.createdBy === 'PERMANENT DELETE') {
          return (
            <b style={{ color: colors.criticality.critical }}>
              {rec.createdBy}
            </b>
          );
        }
        return <span>{rec.createdBy}</span>;
      },
    },
    {
      title: 'Last Sync Date',
      dataIndex: 'lastSync',
      key: 'lastSync',
      sorter: (compA, compB) =>
        compA.lastSync === compB.lastSync
          ? 0
          : compA.lastSync > compB.lastSync
            ? 1
            : -1,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, rec: DeviceKey) => {
        if (rec.createdBy === 'PERMANENT DELETE') {
          return (
            <IBDIcon
              name={'Add'}
              title={'Reinstate Key'}
              onClick={() => handleKeyUpdate(userProfile.email, rec)}
            />
          );
        }
        return (
          <IBDIcon
            name={'Delete'}
            title={'Remove Key'}
            onClick={() => handleKeyUpdate('PERMANENT DELETE', rec)}
          />
        );
      },
    },
  ];
  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={keys}
      loading={loading}
      rowKey={(record) => record.type + record.value}
    />
  );
};
export default DeviceKeysTable;
